import { useState, useEffect, useRef, useCallback } from "react";
import { useMutation, useLazyQuery } from "@apollo/client";
import { useSelector } from "react-redux";
import { useSearchParams, useLocation } from "react-router-dom";
import { styled, keyframes } from "@mui/material/styles";
import { Box, Card, Typography, TextField, IconButton, Fab, Badge, Fade, Chip, CircularProgress } from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { GET_CHAT_HISTORY } from "../../gql/queries/ai";
import { SEND_CHAT_MESSAGE, CLEAR_CHAT_HISTORY } from "../../gql/mutations/ai";
import ProjectSelector from "./ProjectSelector";

// ---------------------------------------------------------------------------
// Keyframe animations
// ---------------------------------------------------------------------------

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.5);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(25, 118, 210, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
`;

const dotBounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
`;

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const WidgetRoot = styled(Box)({
  position: "fixed",
  bottom: 24,
  right: 24,
  zIndex: 1300,
});

const PulsingFab = styled(Fab)(({ theme }) => ({
  animation: `${pulse} 2s infinite`,
  backgroundColor: theme.palette.primary.main,
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const ChatCard = styled(Card)({
  width: 380,
  height: 520,
  display: "flex",
  flexDirection: "column",
  borderRadius: 16,
  overflow: "hidden",
  boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
});

const HeaderBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 16px",
  background: theme.palette.primary.main,
  color: "#fff",
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: prop => prop !== "isUser",
})(({ theme, isUser }) => ({
  maxWidth: "78%",
  padding: "8px 14px",
  borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
  backgroundColor: isUser ? theme.palette.primary.main : theme.palette.grey[100],
  color: isUser ? "#fff" : theme.palette.text.primary,
  wordBreak: "break-word",
  alignSelf: isUser ? "flex-end" : "flex-start",
}));

const TypingDot = styled("span")(({ delay }) => ({
  width: 7,
  height: 7,
  margin: "0 2px",
  borderRadius: "50%",
  backgroundColor: "#999",
  display: "inline-block",
  animation: `${dotBounce} 1.4s infinite ease-in-out both`,
  animationDelay: delay || "0s",
}));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(raw) {
  if (!raw) return "";
  const d = new Date(Number(raw) || raw);
  if (isNaN(d.getTime())) return "";
  const hours = d.getHours().toString().padStart(2, "0");
  const mins = d.getMinutes().toString().padStart(2, "0");
  return `${hours}:${mins}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function AiChatWidget() {
  const userData = useSelector(state => state.reducer.userData);
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Project context: prefer projectId from URL (?projectId= or ?origin= on project-detail)
  const projectIdFromUrl = searchParams.get("projectId") || searchParams.get("origin") || "";
  const [projectId, setProjectId] = useState(projectIdFromUrl);

  // Sync projectId when URL changes (e.g. user navigates to project detail)
  useEffect(() => {
    if (projectIdFromUrl) setProjectId(projectIdFromUrl);
  }, [projectIdFromUrl, location.pathname]);

  // UI state
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState([]);
  const [suggestedFollowUps, setSuggestedFollowUps] = useState([]);
  const [isTyping, setIsTyping] = useState(false);

  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // GQL
  const [loadHistory, { loading: historyLoading }] = useLazyQuery(GET_CHAT_HISTORY, {
    fetchPolicy: "network-only",
    onCompleted: data => {
      if (data?.getChatHistory) {
        const mapped = data.getChatHistory.map(m => ({
          id: m.id,
          role: m.role,
          content: m.content,
          createdAt: m.createdAt,
        }));
        setMessages(mapped);
      }
    },
  });

  const [sendMessage] = useMutation(SEND_CHAT_MESSAGE);
  const [clearHistory] = useMutation(CLEAR_CHAT_HISTORY);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  // Load history when projectId changes
  useEffect(() => {
    if (projectId) {
      loadHistory({ variables: { projectId, limit: 50 } });
      setSuggestedFollowUps([]);
    } else {
      setMessages([]);
      setSuggestedFollowUps([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Focus input when widget opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  // -------------------------------------------
  // Handlers
  // -------------------------------------------

  const handleSend = useCallback(
    async text => {
      const msg = (text || inputValue).trim();
      if (!msg || !projectId) return;

      // Append user message immediately
      const userMsg = {
        id: `temp-${Date.now()}`,
        role: "user",
        content: msg,
        createdAt: new Date().toISOString(),
      };
      setMessages(prev => [...prev, userMsg]);
      setInputValue("");
      setSuggestedFollowUps([]);
      setIsTyping(true);

      try {
        const { data } = await sendMessage({
          variables: { projectId, message: msg },
        });

        if (data?.sendChatMessage) {
          const aiMsg = {
            id: `ai-${Date.now()}`,
            role: "assistant",
            content: data.sendChatMessage.message,
            createdAt: new Date().toISOString(),
          };
          setMessages(prev => [...prev, aiMsg]);

          // Parse suggested follow-ups
          const followUps = data.sendChatMessage.suggestedFollowUps;
          if (followUps) {
            try {
              const parsed = typeof followUps === "string" ? JSON.parse(followUps) : followUps;
              setSuggestedFollowUps(Array.isArray(parsed) ? parsed : []);
            } catch {
              setSuggestedFollowUps([]);
            }
          } else {
            setSuggestedFollowUps([]);
          }
        }
      } catch (err) {
        const errMsg = {
          id: `err-${Date.now()}`,
          role: "assistant",
          content: "Sorry, something went wrong. Please try again.",
          createdAt: new Date().toISOString(),
        };
        setMessages(prev => [...prev, errMsg]);
      } finally {
        setIsTyping(false);
      }
    },
    [inputValue, projectId, sendMessage],
  );

  const handleClear = useCallback(async () => {
    if (!projectId) return;
    try {
      await clearHistory({ variables: { projectId } });
      setMessages([]);
      setSuggestedFollowUps([]);
    } catch {
      // silent
    }
  }, [projectId, clearHistory]);

  const handleKeyDown = e => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // -------------------------------------------
  // Render
  // -------------------------------------------

  return (
    <WidgetRoot>
      {/* Collapsed state — FAB */}
      {!open && (
        <Fade in={!open}>
          <PulsingFab color="primary" aria-label="Open AI Chat" onClick={() => setOpen(true)}>
            <SmartToyIcon sx={{ color: "#fff" }} />
          </PulsingFab>
        </Fade>
      )}

      {/* Expanded state — Chat Card */}
      {open && (
        <Fade in={open}>
          <ChatCard>
            {/* Header */}
            <HeaderBar>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SmartToyIcon fontSize="small" />
                <Typography variant="subtitle1" fontWeight={600}>
                  AI SEO Assistant
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton size="small" sx={{ color: "#fff" }} onClick={handleClear} title="Clear chat history">
                  <DeleteIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" sx={{ color: "#fff" }} onClick={() => setOpen(false)} title="Close chat">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </HeaderBar>

            {/* Project/Website selector */}
            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                Select website to chat about
              </Typography>
              <ProjectSelector value={projectId} onChange={setProjectId} placeholder="Choose a website" size="small" showWebsiteInOption={false} />
            </Box>

            {/* Messages area */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                px: 2,
                py: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              {historyLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              )}

              {!historyLoading && messages.length === 0 && projectId && (
                <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                  <SmartToyIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
                  <Typography variant="body2">Ask me anything about your SEO project!</Typography>
                </Box>
              )}

              {!historyLoading && !projectId && (
                <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                  <Typography variant="body2">Enter a Project ID above to start chatting.</Typography>
                </Box>
              )}

              {messages.map(msg => (
                <Box
                  key={msg.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: msg.role === "user" ? "flex-end" : "flex-start",
                  }}
                >
                  <MessageBubble isUser={msg.role === "user"}>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {msg.content}
                    </Typography>
                  </MessageBubble>
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.3,
                      px: 0.5,
                      color: "text.disabled",
                      fontSize: "0.68rem",
                    }}
                  >
                    {formatTimestamp(msg.createdAt)}
                  </Typography>
                </Box>
              ))}

              {/* Typing indicator */}
              {isTyping && (
                <Box sx={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 0.5, pl: 1 }}>
                  <TypingDot delay="0s" />
                  <TypingDot delay="0.2s" />
                  <TypingDot delay="0.4s" />
                </Box>
              )}

              {/* Suggested follow-ups */}
              {suggestedFollowUps.length > 0 && !isTyping && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
                  {suggestedFollowUps.map(q => (
                    <Chip key={q} label={q} size="small" variant="outlined" color="primary" clickable onClick={() => handleSend(q)} sx={{ fontSize: "0.72rem" }} />
                  ))}
                </Box>
              )}

              <div ref={messagesEndRef} />
            </Box>

            {/* Input area */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1.5,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <TextField
                inputRef={inputRef}
                size="small"
                fullWidth
                placeholder={projectId ? "Type a message..." : "Set project ID first"}
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={!projectId || isTyping}
                variant="outlined"
                multiline
                maxRows={3}
              />
              <IconButton color="primary" onClick={() => handleSend()} disabled={!inputValue.trim() || !projectId || isTyping}>
                <SendIcon />
              </IconButton>
            </Box>
          </ChatCard>
        </Fade>
      )}
    </WidgetRoot>
  );
}

export default AiChatWidget;
